import { Button, DialogActions, Typography } from '@material-ui/core';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import { useMutation } from '@tanstack/react-query';
import React, { useState, useEffect, useRef } from 'react';
import strings from '../../localization';
import { chromeLocalStorage } from '../../utils/chrome-storage-polyfill';
import DialogForm from '../DialogForm';

const useStyles = makeStyles((theme) => ({
  captchaContainer: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  hcaptchaWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
}));

export interface CaptchaSubscribeDialogProps {
  open: boolean;
  onAccepted?: () => void;
  onClose: () => void;
}

export interface CaptchaPrefetchProps {
  created: number;
  count: number;
  hcaptchaToken: string;
}

// Add hCaptcha script to document head
const loadHCaptchaScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.hcaptcha) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load hCaptcha script'));
    document.head.appendChild(script);
  });
};

export default function CaptchaSubscribeDialog({ open, onClose }: CaptchaSubscribeDialogProps) {
  const classes = useStyles();
  const [hcaptchaToken, setHcaptchaToken] = useState<string>('');
  const [verified, setVerified] = useState(false);
  const [hcaptchaLoaded, setHcaptchaLoaded] = useState(false);
  const hcaptchaRef = useRef<string | null>(null);
  const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

  // Load hCaptcha script when dialog opens
  useEffect(() => {
    if (open && !hcaptchaLoaded) {
      loadHCaptchaScript()
        .then(() => {
          setHcaptchaLoaded(true);
        })
        .catch((error) => {
          console.error('Failed to load hCaptcha:', error);
        });
    }
  }, [open, hcaptchaLoaded]);

  // Render hCaptcha when it's loaded and dialog is open
  useEffect(() => {
    if (open && hcaptchaLoaded && window.hcaptcha && !hcaptchaRef.current) {
      try {
        hcaptchaRef.current = window.hcaptcha.render('hcaptcha-container', {
          sitekey: HCAPTCHA_SITE_KEY,
          callback: (token: string) => {
            setHcaptchaToken(token);
          },
          'expired-callback': () => {
            setHcaptchaToken('');
          },
          'error-callback': (error: string) => {
            console.error('hCaptcha error:', error);
            setHcaptchaToken('');
          },
        });
      } catch (error) {
        console.error('Failed to render hCaptcha:', error);
      }
    }
  }, [open, hcaptchaLoaded, HCAPTCHA_SITE_KEY]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setHcaptchaToken('');
      setVerified(false);
    } else {
      // Reset hCaptcha when dialog closes
      if (hcaptchaRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.reset(hcaptchaRef.current);
        } catch (error) {
          console.error('Failed to reset hCaptcha:', error);
        }
      }
    }
  }, [open]);

  const { mutate, isLoading } = useMutation({
    mutationFn: async () => {
      if (!hcaptchaToken) {
        throw new Error('Please complete the captcha verification');
      }
      
      // Simulate verification process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return { accepted: true, userId: Math.floor(Math.random() * 1000000) };
    },
    onSuccess: (resp) => {
      console.log('hCaptcha verification successful');
      const props: CaptchaPrefetchProps = {
        created: Date.now(),
        count: resp.userId,
        hcaptchaToken: hcaptchaToken,
      };

      chromeLocalStorage.set({
        tmpPrefetch: props,
      });
      
      setVerified(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    },
    onError: (error: any) => {
      console.error('hCaptcha verification failed', error);
      // Reset hCaptcha on error
      if (hcaptchaRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.reset(hcaptchaRef.current);
        } catch (resetError) {
          console.error('Failed to reset hCaptcha:', resetError);
        }
      }
      setHcaptchaToken('');
    },
  });

  const handleSubmit = () => {
    if (!hcaptchaToken.trim()) {
      return;
    }
    mutate();
  };

  return (
    <DialogForm open={open} onClose={onClose} onSubmit={handleSubmit}>
      <DialogTitle>
        {strings.humanVerification}
      </DialogTitle>
      <DialogContent>
        {verified ? (
          <div className={classes.captchaContainer}>
            <Typography variant="h6" color="primary">
              {strings.verificationSuccess}
            </Typography>
            <Typography variant="body2">
              {strings.verificationSuccess2}
            </Typography>
          </div>
        ) : (
          <>
            <Typography variant="body2" style={{ marginBottom: 16 }}>
              {strings.pleaseVerifyMsg}
            </Typography>

            <div className={classes.captchaContainer}>
              <div className={classes.hcaptchaWrapper}>
                <div id="hcaptcha-container"></div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {verified ? 'Close' : 'Cancel'}
        </Button>
        {!verified && (
          <Button 
            color="primary" 
            disabled={isLoading || !hcaptchaToken.trim()} 
            onClick={handleSubmit}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        )}
      </DialogActions>
    </DialogForm>
  );
}

// Type declaration for hCaptcha
declare global {
  interface Window {
    hcaptcha: {
      render: (container: string, options: {
        sitekey: string;
        callback: (token: string) => void;
        'expired-callback': () => void;
        'error-callback': (error: string) => void;
      }) => string;
      reset: (widgetId: string) => void;
    };
  }
}